import { useState, useEffect, useCallback, useRef } from "react";
import { getIcds } from "@/services/sintegradaserver/icd/getIcds";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";

import { SelectComponent, SelectComponentProps } from "@/components/SelectComponent";

type Props = SelectComponentProps;

export const defaultIcd = {
	label: "Todos",
	value: "",
};

export function IcdSelect({ ...rest }: Props) {
	const { ibgeCode } = useCurrentAccount();
	const [options, setOptions] = useState<Array<{ label: string; value: string }>>([defaultIcd]);
	const [isLoading, setIsLoading] = useState(false);
	const [hasMore, setHasMore] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [searchTerm, setSearchTerm] = useState("");
	const abortControllerRef = useRef<AbortController | null>(null);

	// Função para buscar CIDs
	const fetchIcds = useCallback(
		async (page: number = 1, search: string = "") => {
			// Cancela requisição anterior se existir
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}

			// Cria novo controller para esta requisição
			abortControllerRef.current = new AbortController();

			try {
				setIsLoading(true);
				const response = await getIcds({
					ibgeCode,
					page,
					search,
					per_page: 100,
					signal: abortControllerRef.current.signal,
				});

				if (response && response.data && Array.isArray(response.data)) {
					return {
						results: response.data,
						meta: response.meta || { current_page: 1, last_page: 1 },
					};
				}

				return { results: [], meta: { current_page: 1, last_page: 1 } };
			} catch (error: any) {
				if (error?.name === "AbortError") {
					return null;
				}

				return { results: [], meta: { current_page: 1, last_page: 1 } };
			} finally {
				setIsLoading(false);
			}
		},
		[ibgeCode]
	);

	// Função para carregar opções iniciais
	const loadInitialOptions = useCallback(async () => {
		const data = await fetchIcds(1, "");

		if (data && data.results && Array.isArray(data.results)) {
			const icdOptions = data.results.map((icd) => ({
				label: `${icd.icd_code} - ${icd.description_code}`,
				value: icd.icd_code,
			}));

			setOptions([defaultIcd, ...icdOptions]);
			setHasMore(data.meta?.current_page < data.meta?.last_page);
			setCurrentPage(1);
		} else {
			console.log("Nenhum CID encontrado"); // Debug
			// Fallback para dados vazios
			setOptions([defaultIcd]);
			setHasMore(false);
			setCurrentPage(1);
		}
	}, [fetchIcds]);

	// Função para buscar com termo de pesquisa
	const searchIcds = useCallback(
		async (search: string) => {
			console.log("Buscando por:", search); // Debug

			if (search.length < 3) {
				// Se menos de 3 caracteres, carrega opções iniciais
				await loadInitialOptions();
				return;
			}

			const data = await fetchIcds(1, search);
			if (data && data.results && Array.isArray(data.results)) {
				const icdOptions = data.results.map((icd) => ({
					label: `${icd.icd_code} - ${icd.description_code}`,
					value: icd.icd_code,
				}));
				console.log(`Encontrados ${icdOptions.length} CIDs para "${search}"`); // Debug
				setOptions([defaultIcd, ...icdOptions]);
				setHasMore(data.meta?.current_page < data.meta?.last_page);
				setCurrentPage(1);
			} else {
				console.log(`Nenhum CID encontrado para "${search}"`); // Debug
				// Fallback para busca sem resultados
				setOptions([defaultIcd]);
				setHasMore(false);
				setCurrentPage(1);
			}
		},
		[fetchIcds, loadInitialOptions]
	);

	// Função para carregar mais opções (pagination)
	const loadMoreOptions = useCallback(async () => {
		if (!hasMore || isLoading) return;

		const nextPage = currentPage + 1;
		const data = await fetchIcds(nextPage, searchTerm);
		if (data && data.results && Array.isArray(data.results)) {
			const icdOptions = data.results.map((icd) => ({
				label: `${icd.icd_code} - ${icd.description_code}`,
				value: icd.icd_code,
			}));
			setOptions((prev) => [...prev, ...icdOptions]);
			setHasMore(data.meta?.current_page < data.meta?.last_page);
			setCurrentPage(nextPage);
		} else {
			// Se não há mais dados, desabilita paginação
			setHasMore(false);
		}
	}, [hasMore, isLoading, currentPage, searchTerm, fetchIcds]);

	// Efeito para carregar opções iniciais
	useEffect(() => {
		if (ibgeCode) {
			loadInitialOptions();
		}
	}, [ibgeCode, loadInitialOptions]);

	// Efeito para buscar com debounce (removido para busca imediata)
	// useEffect(() => {
	// 	if (debouncedSearch !== searchTerm) {
	// 		setSearchTerm(debouncedSearch);
	// 		searchIcds(debouncedSearch);
	// 	}
	// }, [debouncedSearch, searchIcds]);

	// Cleanup ao desmontar o componente
	useEffect(() => {
		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
		};
	}, []);

	// Função para lidar com mudança no input
	const handleInputChange = (inputValue: string) => {
		console.log("Input mudou para:", inputValue); // Debug
		setSearchTerm(inputValue);

		// Busca imediata se tiver 3+ caracteres
		if (inputValue.length >= 3) {
			searchIcds(inputValue);
		} else if (inputValue.length === 0) {
			// Se limpar o input, volta para opções iniciais
			loadInitialOptions();
		} else if (inputValue.length > 0 && inputValue.length < 3) {
			// Para 1-2 caracteres, filtra localmente as opções já carregadas
			filterLocalOptions(inputValue);
		}
	};

	// Função para filtrar opções localmente
	const filterLocalOptions = useCallback(
		(searchTerm: string) => {
			if (options.length <= 1) return; // Só "Todos"

			const filteredOptions = options.filter(
				(option) =>
					option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
					option.value.toLowerCase().includes(searchTerm.toLowerCase())
			);

			// Mantém "Todos" sempre na primeira posição
			const todosOption = options.find((opt) => opt.value === "");
			const otherOptions = filteredOptions.filter((opt) => opt.value !== "");

			setOptions(todosOption ? [todosOption, ...otherOptions] : otherOptions);
		},
		[options]
	);

	// Função para lidar com scroll (pagination)
	const handleMenuScrollToBottom = () => {
		loadMoreOptions();
	};

	return (
		<SelectComponent
			className="fs-8"
			placeholder="Selecione..."
			options={options}
			isLoading={isLoading}
			onInputChange={handleInputChange}
			onMenuScrollToBottom={handleMenuScrollToBottom}
			filterOption={() => true} // Desabilita filtro interno, usa nosso próprio
			{...rest}
		/>
	);
}
